import React, { useState, useEffect } from "react";
import { Container, Button, Typography, Paper, Box, CircularProgress, Modal } from "@mui/material";
import { Html5QrcodeScanner } from "html5-qrcode";
import { motion } from "framer-motion";

const App = () => {
  const [scanResult, setScanResult] = useState("");
  const [error, setError] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [scannerInitialized, setScannerInitialized] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const initializeScanner = () => {
    const newScanner = new Html5QrcodeScanner(
      "reader",
      { fps: 10, qrbox: 250 },
      false
    );

    const onScanSuccess = (decodedText) => {
      setIsScanning(false);
      newScanner.clear().catch(console.error); // Clear the scanner

      fetch("https://backendscan.advento.site/scan", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ serialNumber: decodedText }),
      })
        .then((response) => response.json())
        .then((data) => {
          setScanResult(data.message);
          setOpenModal(true); // Show the modal with the scan result
        })
        .catch((err) => setError("Error contacting the server."));
    };

    const onScanFailure = (error) => {
      console.warn(`Scan failed: ${error}`);
    };

    newScanner.render(onScanSuccess, onScanFailure);

    setScanner(newScanner); // Save the scanner instance
  };

  const startScanning = () => {
    setScanResult("");  // Clear previous scan result
    setError("");  // Clear previous error
    setIsScanning(true);
    setScannerInitialized(true);
  };

  const stopScanning = () => {
    if (scanner) {
      scanner.clear().catch(console.error); // Clear the scanner when stopping
    }
    setIsScanning(false);
    setScannerInitialized(false);
  };

  const refreshPage = () => {
    window.location.reload();  // Reload the page to reset the app
  };

  useEffect(() => {
    if (scannerInitialized) {
      initializeScanner(); // Initialize the scanner when it's ready
    } else {
      stopScanning(); // Clean up scanner when not scanning
    }

    return () => {
      if (scanner) {
        scanner.clear().catch(console.error); // Ensure scanner is cleared on cleanup
      }
    };
  }, [scannerInitialized]);

  return (
    <Container maxWidth="sm" sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", padding: 2 }}>
      <Paper elevation={5} sx={{ padding: 3, width: "100%", textAlign: "center", borderRadius: "12px" }}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
          <Typography variant="h4" sx={{ marginBottom: 2, fontWeight: "bold" }}>
            Event QR Code Scanner
          </Typography>
        </motion.div>

        <Box id="reader" sx={{ width: "100%", marginBottom: 2 }}></Box>

        {isScanning ? (
          <CircularProgress size={60} sx={{ color: "#4caf50" }} />
        ) : (
          <Button
            variant="contained"
            color="primary"
            sx={{ marginBottom: 2, borderRadius: "8px", fontSize: "16px", padding: "10px 20px" }}
            onClick={startScanning}
          >
            Start Scanning
          </Button>
        )}

        <Modal
          open={openModal}
          onClose={() => setOpenModal(false)}
          aria-labelledby="scan-result-modal"
          aria-describedby="scan-result-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 300,
              bgcolor: "background.paper",
              borderRadius: "12px",
              boxShadow: 24,
              padding: 2,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2, fontWeight: "bold" }}>
              {scanResult}
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              onClick={refreshPage}
              sx={{ marginTop: 2, borderRadius: "8px" }}
            >
              Refresh
            </Button>
          </Box>
        </Modal>

        {error && (
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Typography variant="h6" color="error.main">
              {error}
            </Typography>
          </motion.div>
        )}
      </Paper>
    </Container>
  );
};

export default App;
